import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NotificationItem, LocationTargetingMode } from '@/lib/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, Link } from '@tanstack/react-router';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';
import { useState } from 'react';
import { MarkdownContent, markdownRehypePlugins } from '@/components/ui/markdown-content';
import { ArrowRight } from 'lucide-react';
import {
  useUpdateGeoTargetingModeMutation,
  useAddExcludedLocationMutation,
  useRemoveExcludedLocationMutation,
} from '@/lib/query-options';
import { toast } from '@/hooks/useToast';

const GEO_MODE_LABEL: Record<string, string> = {
  PRESENCE: 'Presence only',
  PRESENCE_OR_INTEREST: 'Presence or interest',
};

// Note: the notification title is rendered with the same sanitize pipeline as
// the body (markdownRehypePlugins) so both render sites stay aligned (#4828).

interface NotificationDetailProps {
  notification: NotificationItem | null;
  isLoading?: boolean;
}

export function NotificationDetail({ notification, isLoading }: NotificationDetailProps) {
  const navigate = useNavigate();
  const [isGoogleAdsModalOpen, setIsGoogleAdsModalOpen] = useState(false);
  const geoSlug = (notification?.item_metadata?.slug as string) ?? '';
  const geoMutation = useUpdateGeoTargetingModeMutation(geoSlug);
  const exclusionCampaignId = Number(notification?.item_metadata?.campaign_id) || 0;
  const addExclusion = useAddExcludedLocationMutation(exclusionCampaignId);
  const removeExclusion = useRemoveExcludedLocationMutation(exclusionCampaignId);

  const handleSetItBack = () => {
    const meta = notification?.item_metadata;
    if (!meta) return;
    const campaignId = Number(meta.campaign_id);
    const field = meta.field as 'positive_geo_target_type' | 'negative_geo_target_type';
    const previous = meta.old as LocationTargetingMode;
    if (!campaignId || !field || !previous) return;
    geoMutation.mutate(
      { campaignId, body: { [field]: previous } },
      {
        onError: (error) =>
          toast({
            title: 'Failed to revert location targeting',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          }),
      }
    );
  };

  const handleExclusionSetItBack = () => {
    const meta = notification?.item_metadata;
    if (!meta) return;
    const criteriaId = Number(meta.criteria_id);
    const change = meta.change as 'added' | 'removed';
    if (!exclusionCampaignId || !criteriaId || !change) return;
    const opts = {
      onError: (error: unknown) =>
        toast({
          title: 'Failed to revert exclusion',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive' as const,
        }),
    };
    // GA added it (DB mirrors) -> set it back = remove. GA removed it -> set it back = re-add.
    if (change === 'added') removeExclusion.mutate(criteriaId, opts);
    else addExclusion.mutate(criteriaId, opts);
  };

  const handleButtonClick = (buttonAction: string) => {
    if (buttonAction === 'connect_google_ads') {
      setIsGoogleAdsModalOpen(true);
    } else if (buttonAction === 'create_campaigns') {
      navigate({ to: '/new/onboarding' });
    }
  };

  const handleCloseGoogleAdsModal = () => {
    setIsGoogleAdsModalOpen(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className='p-6'>
          <div className='flex items-center justify-center text-gray-500'>
            <span>Loading notification...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!notification) {
    return (
      <Card>
        <CardContent className='p-6'>
          <div className='flex items-center justify-center text-gray-500'>
            <span>Select a notification to view details</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className='p-6 space-y-4 max-w-[720px]'>
          {/* Notification Title */}
          <h2 className='text-xl font-semibold text-[#0f172a] prose prose-sm max-w-none [&_a]:text-[#290BD9] [&_a]:underline [&_a:hover]:text-[#2309C5] [&_a]:no-underline'>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={markdownRehypePlugins}>
              {notification.title}
            </ReactMarkdown>
          </h2>

          {/* Notification Content */}
          <MarkdownContent content={notification.content} />

            {/* Action buttons section */}
            {(notification.item_metadata?.button_text || notification.item_metadata?.link_url) && (
              <div className='mt-6 pt-4 border-t border-gray-200'>
                <div className='flex gap-3'>
                  {notification.item_metadata?.link_url && (
                    <Link to={notification.item_metadata.link_url as string}>
                      <Button className='bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center gap-2'>
                        <span>View Campaign</span>
                        <ArrowRight className='h-4 w-4' />
                      </Button>
                    </Link>
                  )}
                  {notification.item_metadata?.button_text && notification.item_metadata?.button_action && (
                    <Button
                      onClick={() => {
                        const action = notification.item_metadata.button_action;
                        if (action && typeof action === 'string') {
                          handleButtonClick(action);
                        }
                      }}
                      variant={notification.item_metadata?.link_url ? 'outline' : 'default'}
                      className={notification.item_metadata?.link_url ? '' : 'bg-[#290BD9] hover:bg-[#2309C5] text-white'}
                    >
                      {notification.item_metadata.button_text}
                    </Button>
                  )}
                </div>
                {/* Per-campaign links for multi-campaign notifications */}
                {Array.isArray(notification.item_metadata?.campaign_links) &&
                  (notification.item_metadata.campaign_links as Array<{ campaign_name: string; link_url: string }>).length > 1 && (
                  <div className='mt-3'>
                    <p className='text-sm font-medium text-gray-700 mb-1.5'>Affected Campaigns:</p>
                    <div className='flex flex-col gap-1'>
                      {(notification.item_metadata.campaign_links as Array<{ campaign_name: string; link_url: string }>).map(
                        (campaignLink) => (
                          <Link
                            key={campaignLink.link_url}
                            to={campaignLink.link_url}
                            className='text-sm text-[#290BD9] hover:text-[#2309C5] hover:underline'
                          >
                            {campaignLink.campaign_name} →
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reverse-sync "Set it back" inline action */}
            {notification.source === 'location_setting_change' &&
              notification.item_metadata?.actor === 'reverse_sync' &&
              typeof notification.item_metadata?.field === 'string' &&
              typeof notification.item_metadata?.old === 'string' && (
                <div className='mt-6 pt-4 border-t border-gray-200'>
                  <Button variant='outline' disabled={geoMutation.isPending} onClick={handleSetItBack}>
                    {geoMutation.isPending
                      ? 'Reverting...'
                      : `Set it back to ${
                          GEO_MODE_LABEL[notification.item_metadata.old as string] ?? notification.item_metadata.old
                        }`}
                  </Button>
                </div>
              )}

            {/* Reverse-sync "Set it back" for location exclusions */}
            {notification.source === 'location_exclusion_change' &&
              notification.item_metadata?.actor === 'reverse_sync' &&
              typeof notification.item_metadata?.criteria_id !== 'undefined' &&
              typeof notification.item_metadata?.change === 'string' && (
                <div className='mt-6 pt-4 border-t border-gray-200'>
                  <Button
                    variant='outline'
                    disabled={addExclusion.isPending || removeExclusion.isPending}
                    onClick={handleExclusionSetItBack}
                  >
                    {notification.item_metadata.change === 'added'
                      ? `Set it back (remove exclusion: ${notification.item_metadata.location_name ?? 'location'})`
                      : `Set it back (re-exclude ${notification.item_metadata.location_name ?? 'location'})`}
                  </Button>
                </div>
              )}

            {/* <div className='mt-6 pt-4 border-t border-gray-200'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-900'>Was this notification helpful?</span>
                <div className='flex items-center space-x-2'>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleThumbsUp}
                    className={`flex items-center space-x-1 ${
                      userFeedback === 'thumbs_up'
                        ? 'text-green-600 bg-green-50 hover:bg-green-100'
                        : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                    }`}
                  >
                    <ThumbsUp className='h-4 w-4' />
                    <span>Yes</span>
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleThumbsDown}
                    className={`flex items-center space-x-1 ${
                      userFeedback === 'thumbs_down'
                        ? 'text-red-600 bg-red-50 hover:bg-red-100'
                        : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <ThumbsDown className='h-4 w-4' />
                    <span>No</span>
                  </Button>
                </div>
              </div>
            </div> */}
        </CardContent>
      </Card>

      <GoogleAdAccountSelectionModal isOpen={isGoogleAdsModalOpen} onClose={handleCloseGoogleAdsModal} />
    </>
  );
}
